import { Constants } from "appConstants";
import { FormikProps } from "formik";
import { TollStation } from "models/TollStation";
import { ApplicationStatus, ConcessionaireProps, ExemptionTypeData, RegulatoryAgencyProps } from "models/types";
import { ApplicationFormDocumentFolder, DocumentLike } from "services/types";

type SimpleSpread<L, R> = R & Pick<L, Exclude<keyof L, keyof R>>;

export enum PersonTypeOptions {
	physical = "PF",
	juridical = "PJ"
}

export enum ApplicantDocumentType {
	CPF = "CPF",
	CNPJ = "CNPJ"
}

export enum ApplicantDataFields {
	applicantType = "applicantType",

	applicantAddressPostalCode = "applicantAddressPostalCode",
	applicantAddressState = "applicantAddressState",
	applicantAddressCity = "applicantAddressCity",
	applicantAddressNeighbourhood = "applicantAddressNeighbourhood",
	applicantAddressStreet = "applicantAddressStreet",
	applicantAddressNumber = "applicantAddressNumber",
	applicantAddressComplement = "applicantAddressComplement",

	applicantName = "applicantName",
	applicantPhoneNumber = "applicantPhoneNumber",
	applicantEmail = "applicantEmail",
	applicantDocument = "applicantDocument"
}

export interface ApplicantData {
	id?: string;
	applicantType: PersonTypeOptions;

	applicantAddressPostalCode: string | null;
	applicantAddressState: Constants.FederalUnits | null;
	applicantAddressCity: string | null;
	applicantAddressNeighbourhood: string | null;
	applicantAddressStreet: string | null;
	applicantAddressNumber: string | null;
	applicantAddressComplement: string | null;

	applicantName: string | null;
	applicantPhoneNumber: string | null;
	applicantEmail: string | null;
	applicantDocument: string | null;
	applicantDocumentType: ApplicantDocumentType;
}

export enum ApplicationVehicleFields {
	plate = "plate",
	renavamCode = "renavamCode",
	numberOfAxis = "numberOfAxis",
	vehicleBrand = "vehicleBrand",
	vehicleModel = "vehicleModel",
	color = "color",
	manufactureYear = "manufactureYear",
	modelYear = "modelYear",
	tagNumber = "tagNumber",
	tagCompany = "tagCompany",
	tagSelection = "tagSelection",
	foundPlateTagsCount = "foundPlateTagsCount"
}

export enum ApplicationDocumentValidationStatus {
	NOT_VALIDATED = "NOT_VALIDATED",
	VALIDATION_SUCCESS = "VALIDATION_SUCCESS",
	VALIDATION_ERROR = "VALIDATION_ERROR",
	API_UNAVAILABLE = "API_UNAVAILABLE"
}

export interface ApplicationVehicle {
	id?: string;
	plate: string | null;
	renavamCode: string | null;
	numberOfAxis: number | null;
	vehicleBrand: string | null;
	vehicleModel: string | null;
	color: string | null;
	manufactureYear: number | null;
	modelYear: number | null;
	tagNumber: string | null;
	documentValidationStatus: ApplicationDocumentValidationStatus;
	tagCompany: TagCompany | null;
	tagSelection: string | null;
	foundPlateTagsCount: number;
	fieldsMissingFromApi: string[];
}

export enum ApplicationVehicleTreatedNames {
	plate = "Placa do veículo",
	renavamCode = "Renavam",
	vehicleModel = "Modelo do veículo",
	color = "Cor do veículo",
	manufactureYear = "Ano fabricação",
	modelYear = "Ano modelo",
	tagNumber = "Número da TAG",
	tagCompany = "Empresa responsável"
}

export interface TagCompany {
	id: string;
	name: string;
	code: string;
}

export interface ApplicationDocument extends DocumentLike {
	id?: string;
}

export enum ApplicationFields {
	status = "status",
	concessionaire = "concessionaire",
	tollStations = "tollStations",
	exemptionType = "exemptionType",
	applicantData = "applicantData",
	documentFolders = "documentFolders",
	description = "description"
}

export interface ApplicationData {
	id?: string;
	applicationProcessCode?: string;
	status: ApplicationStatus;
	tollStations: TollStation[] | null;
	concessionaire: ConcessionaireProps | null;
	exemptionType: ExemptionTypeData | null;
	applicantData: ApplicantData;
	applicationVehicle: ApplicationVehicle;
	documentFolders: ApplicationFormDocumentFolder[];
}

export type ApplicationFormData = SimpleSpread<
	ApplicationData,
	{
		documentFolders: Record<string, ApplicationFormDocumentFolder>;
	}
>;

export interface ApplicationProps extends ApplicationData {
	applicationProcessCode?: string;
	highwayName?: string;
	highwayCode?: string;
	concessionaireName?: string;
	issuedAt?: string;
	exemptionTypeName?: string;
	currentUserResponsibleEmail?: string;
	currentUserResponsibleGroup?: Constants.UserRoleGroups;
	currentResponsibleUserName?: string;
	regulatoryAgencyName?: string;
	regulatoryAgency?: RegulatoryAgencyProps;
	analysisLimitDatetime?: string;
}

export enum ApplicationStep {
	ConcessionaireStep = 0,
	Exemption = 1,
	ApplicationDocuments = 2,
	ApplicationData = 3,
	ApplicationReview = 4
}

export interface ApplicationFormik {
	formik: FormikProps<ApplicationFormData>;
}
